import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button, Input, Spinner } from '@pijma/crypto'
import { useNumericMask } from '@pijma/input'
import { SourceInfo } from '@pijma/input/lib/dts/hooks/utils/types'

import { sellApplicationClient } from '@api'
import { TFailureResponse } from '@api/types'
import { Dispatch, RootState } from '@stores'
import { ECurrency, isoToECurrency, TPayFormState } from '@stores/types/*'
import {
  extractCurrencySymbol,
  localeNumber,
  removeLeadingZeroes,
} from '@utils'
import { HttpStatusCode } from 'axios'
import styled from 'styled-components'

import { EOperationErrorCause } from '../../../../api/types/EOperationErrorCause'
import { EPaymentProvider } from '../../../../stores/types/EPaymentProvider'
import { TPaymentGateway } from '../../../../stores/types/TPaymentGatewayState'
import { H4 } from '../../../common/header/Headings'
import { Island } from '../../../common/island'
import { FlexBoxCenteredLayout } from '../../../common/layout/flex-box'
import NavigationBar from '../../../common/navigation/NavigationBar'
import { BodyText500, CaptionText500 } from '../../../common/text/BodyText'
import { getToSourcePath } from '../sell-provider-select-page/SellProviderSelectPage'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 20px;
`

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px 24px;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 32px 20px 20px;
`

const RateTipContainer = styled.div`
  color: #868686;
  padding: 0 32px;
  text-align: center;
`

const SourceTitle = styled.div`
  display: flex;
  width: 100%;
  padding: 20px 24px 16px;
`

const NavigationBarWrapper = styled.div`
  padding: 0 4px;
  display: flex;
  width: 100%;
  align-items: start;
`

const BackContainer = styled.div`
  margin: 24px 0 0 0;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

interface StateProps {
  payForm: TPayFormState
  prettyRate: number
}

interface DispatchProps {
  setAmount: (amountFrom: string, amountTo: string) => void
  doPaymentBlocking: () => void
  resetPaymentBlocking: () => void
}

type Props = StateProps & DispatchProps

const trueAmount = (formattedAmount: string) => {
  return formattedAmount.replace(',', '.').replaceAll(' ', '')
}

// Только сумму с запятой можно вставлять в поле для дальнейшего форматирования. Точка теряется.
// Костыль, но лучше не придумал, да и вроде как пофиг
const fixableAmount = (formattedAmount: string | undefined) => {
  if (formattedAmount == undefined) return ''
  return formattedAmount
    .replace('.', ',')
    .replaceAll(' ', '')
    .replace(/(\d+),(\d\d)\d*/g, '$1,$2')
}

const convertWithRate = (amount: string, rate: number) => {
  return fixableAmount(String(+amount * rate))
}

const convertWithReverseRate = (amount: string, rate: number) => {
  return convertWithRate(amount, 1 / rate)
}

const SellSumPage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()

  const [prettyAmountCurrencyFrom, setPrettyAmountCurrencyFrom] = useState('')
  const [prettyAmountCurrencyTo, setPrettyAmountCurrencyTo] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    const amount = +trueAmount(prettyAmountCurrencyTo)
    const amountOutsideLimits =
      amount &&
      (amount < +(props.payForm.minLimit as string) ||
        amount > +(props.payForm.maxLimit as string))
    const incorrectAmount = Number.isNaN(amount)

    if (amountOutsideLimits) {
      setErrorMessage(
        `${intl.formatMessage({
          id: 'sellSumPage.amountOutsideLimits',
        })} ${localeNumber(props.payForm.minLimit)} ${intl.formatMessage({
          id: 'sellSumPage.to',
        })} ${localeNumber(props.payForm.maxLimit)} ${isoToECurrency(
          props.payForm.paymentGateway?.currencyTo,
        )}`,
      )
    } else if (incorrectAmount) {
      setErrorMessage(
        intl.formatMessage({ id: 'sellSumPage.notLooksLikeAmount' }),
      )
    } else {
      setErrorMessage('')
    }
  }, [
    prettyAmountCurrencyTo,
    props.payForm.maxLimit,
    props.payForm.minLimit,
    props.payForm.application?.currency,
  ])

  const maskPropsCurrencyFrom = useNumericMask({
    value: prettyAmountCurrencyFrom,
    decimalScale: 2,
    allowedDecimalSeparators: ['%', '.', ',', 'б', 'ю'],
    decimalSeparator: ',',
    thousandsGroupStyle: 'thousand',
    thousandSeparator: '',
    staticSeparator: true,
    allowLeadingZeros: false,
    allowNegative: false,
    onValueChange: ({ formattedValue }, sourceInfo: SourceInfo) => {
      formattedValue = removeLeadingZeroes(formattedValue)
      if (sourceInfo.event) {
        const amountFrom = trueAmount(formattedValue)
        const convertedAmountTo = convertWithRate(amountFrom, props.prettyRate)
        props.setAmount(amountFrom, trueAmount(convertedAmountTo))
        setPrettyAmountCurrencyTo(convertedAmountTo)
      }
      setPrettyAmountCurrencyFrom(formattedValue)
    },
  })

  const maskPropsCurrencyTo = useNumericMask({
    value: prettyAmountCurrencyTo,
    decimalScale: 2,
    allowedDecimalSeparators: ['%', '.', ',', 'б', 'ю'],
    decimalSeparator: ',',
    thousandsGroupStyle: 'thousand',
    thousandSeparator: '',
    staticSeparator: true,
    allowLeadingZeros: false,
    allowNegative: false,
    onValueChange: ({ formattedValue }, sourceInfo: SourceInfo) => {
      formattedValue = removeLeadingZeroes(formattedValue)
      if (sourceInfo.event) {
        const amountTo = trueAmount(formattedValue)
        const convertedAmountFrom = convertWithReverseRate(
          amountTo,
          props.prettyRate,
        )
        setPrettyAmountCurrencyFrom(convertedAmountFrom)
        props.setAmount(trueAmount(convertedAmountFrom), amountTo)
      }
      setPrettyAmountCurrencyTo(formattedValue)
    },
  })

  const applicationId = props.payForm?.application?.applicationUuid as string
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const catchReason = (reason: any) => {
    props.resetPaymentBlocking()
    const failureResponse = reason.response.data as TFailureResponse
    if (
      failureResponse.errorCode === EOperationErrorCause.NO_OFFERS_AVAILABLE
    ) {
      redirectTo(`../../application/${applicationId}/sell/offers-not-found`)
      return
    }
    if (failureResponse.errorCode === EOperationErrorCause.INSUFFICIENT_FUNDS) {
      redirectTo(`../../application/${applicationId}/sell/insufficient-funds`)
    }
    console.log('something going wrong with payment', reason)
    if (reason.response.status === HttpStatusCode.Unauthorized) {
      redirectTo(`../../timeout`)
    } else {
      redirectTo(`../../failure`)
    }
  }

  /* eslint-disable sonarjs/cognitive-complexity */
  const proceedSell = () => {
    if (props.payForm.paymentGateway?.provider === EPaymentProvider.PW) {
      if (!props.payForm.application?.applicationUuid)
        throw 'no applicationUuid'
      if (!props.payForm.amountFrom) throw 'no amountFrom'
      if (!props.payForm.amountTo) throw 'no amountTo'
      if (!props.payForm.paymentGateway) throw 'no gateway'
      if (!props.payForm.receiver) throw 'no receiver'
      if (!props.payForm.receiverBankCard) throw 'no receiverBankCard'
      if (!props.payForm.paymentGateway.paymentGatewayId)
        throw 'no paymentGatewayId'
      props.doPaymentBlocking()
      sellApplicationClient
        .sellCard(
          props.payForm.application.applicationUuid,
          props.payForm.amountFrom,
          props.payForm.amountTo,
          props.payForm.receiver,
          props.payForm.receiverBankCard,
          props.payForm.paymentGateway.paymentGatewayId,
        )
        .then((res) => {
          window.location.replace(res.redirectUrl)
        })
        .catch((reason) => catchReason(reason))
    } else if (
      props.payForm.paymentGateway?.provider === EPaymentProvider.SPAYZ
    ) {
      redirectTo('../confirm')
    } else {
      redirectTo('../find-offers')
    }
  }

  const currencyTo = extractCurrencySymbol(
    props.payForm.paymentGateway?.currencyTo,
  )

  const currencyFrom = extractCurrencySymbol(
    props.payForm.paymentGateway?.currencyFrom,
  )

  const currencyToLabel =
    extractCurrencySymbol(props.payForm.paymentGateway?.currencyTo) === '₽'
      ? `${intl.formatMessage({ id: 'sellSumPage.rubles' })}`
      : currencyTo

  const currencyFromLabel =
    extractCurrencySymbol(props.payForm.paymentGateway?.currencyFrom) ===
    ECurrency.USDT_TRC20
      ? 'USDT'
      : currencyFrom

  const dataNavigationLink = getToSourcePath(
    props.payForm.paymentGateway as TPaymentGateway,
  )

  const showMethodNavigation =
    props.payForm.paymentGateway?.provider === EPaymentProvider.SPAYZ
  const methodNavigationLink = showMethodNavigation ? '../method' : ''

  const backLink = showMethodNavigation
    ? methodNavigationLink
    : dataNavigationLink

  return (
    <FlexBoxCenteredLayout>
      <Container>
        <NavigationBarWrapper>
          <NavigationBar
            dataRedirectLink={dataNavigationLink}
            paymentDisabled
            showMethod={showMethodNavigation}
            methodRedirectLink={methodNavigationLink}
            noPaymentMethod
          />
        </NavigationBarWrapper>
        <Island size="M" bgColor="primary">
          <SourceTitle data-testid="amount-page.header">
            <H4>{intl.formatMessage({ id: 'sellSumPage.title' })}</H4>
          </SourceTitle>
          <>
            <InputContainer data-testid="amount-page.input.currencyFrom">
              <Input
                {...maskPropsCurrencyFrom}
                label={currencyFromLabel}
                value={prettyAmountCurrencyFrom}
                width={'100%'}
                description={`1 ${currencyFromLabel} = ${fixableAmount(
                  props.prettyRate?.toString(),
                )} ${currencyTo}`}
                disabled={props.payForm.paymentBlocking}
                type={'text'}
                inputMode={'decimal'}
                placeholder={'0'}
                allowClear={true}
              />
            </InputContainer>{' '}
            <InputContainer data-testid="amount-page.input.currencyTo">
              <Input
                {...maskPropsCurrencyTo}
                label={currencyToLabel}
                value={prettyAmountCurrencyTo}
                width={'100%'}
                errorMessage={errorMessage}
                description={`${localeNumber(
                  props.payForm.minLimit,
                )} – ${localeNumber(props.payForm.maxLimit)} ${currencyTo}`}
                invalid={errorMessage != ''}
                disabled={props.payForm.paymentBlocking}
                type={'text'}
                inputMode={'decimal'}
                placeholder={'0'}
                allowClear={true}
              />
            </InputContainer>
          </>
          <RateTipContainer>
            <CaptionText500>
              {intl.formatMessage({ id: 'sellSumPage.rateTip' })}
            </CaptionText500>
          </RateTipContainer>
          <ButtonContainer data-testid="amount-page.button.forward">
            <Button
              width={'100%'}
              size={'m'}
              disabled={
                props.payForm.paymentBlocking ||
                !+(props.payForm.amountFrom as string) ||
                errorMessage != ''
              }
              onPress={() => proceedSell()}
            >
              {props.payForm.paymentBlocking ? (
                <Spinner size={28} color={'#fff'} />
              ) : (
                intl.formatMessage({ id: 'sellSumPage.sell.btn' })
              )}
            </Button>
          </ButtonContainer>
        </Island>
        <BackContainer onClick={() => redirectTo(backLink)}>
          <BodyText500>
            {intl.formatMessage({ id: 'sellSumPage.sell.back' })}
          </BodyText500>
        </BackContainer>
      </Container>
    </FlexBoxCenteredLayout>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  payForm: state.payForm,
  prettyRate: state.payForm?.rate as number,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setAmount: (amountFrom: string, amountTo: string) =>
      dispatch.payForm.set({
        amountFrom,
        amountTo,
      }),

    doPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: true,
      }),
    resetPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: false,
      }),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SellSumPage)
